// lib/latency-tester.js

import { ajax } from "discourse/lib/ajax";

export class LatencyTester {
  /**
   * Pings a URL via our server-side proxy to avoid CORS issues.
   * Direct browser fetches to provider sites (Cloudflare-protected etc.)
   * are blocked by CORS, so we route through our own server.
   * @param {string} url - The URL to ping
   * @param {number} timeoutMs - Max time to wait (unused, server has 5s timeout)
   * @returns {Promise<number>} Latency in ms
   */
  static async ping(url, timeoutMs = 5000) {
    try {
      const result = await ajax("/benchmarks/proxy-ping", {
        type: "POST",
        data: { url: url },
      });
      if (result.status === "ok") {
        return result.latency;
      }
      // Server reached the target but it was slow — still count it
      if (result.latency > 0) {
        return result.latency;
      }
      throw new Error(result.error || "Ping failed");
    } catch (err) {
      if (err.status === 429) {
        throw new Error("Rate limited — too many pings");
      }
      throw err;
    }
  }

  /**
   * Runs multiple pings to a URL and returns statistics.
   * @param {string} url - The URL to test
   * @param {number} pings - Number of pings to send
   * @param {number} intervalMs - Wait time between pings
   * @returns {Promise<Object>} Statistics object (min, max, avg, jitter, successRate)
   */
  static async runSuite(url, pings = 5, intervalMs = 500) {
    const latencies = [];
    let failures = 0;

    for (let i = 0; i < pings; i++) {
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
      
      try {
        const ms = await this.ping(url);
        latencies.push(ms);
      } catch (err) {
        failures++;
      }
    }

    if (latencies.length === 0) {
      return { successRate: 0, error: 'All pings failed' };
    }

    const min = Math.min(...latencies);
    const max = Math.max(...latencies);
    const sum = latencies.reduce((a, b) => a + b, 0);
    const avg = sum / latencies.length;
    
    // Calculate Jitter (average of absolute differences between consecutive latencies)
    let jitterSum = 0;
    for (let i = 1; i < latencies.length; i++) {
      jitterSum += Math.abs(latencies[i] - latencies[i - 1]);
    }
    const jitter = latencies.length > 1 ? jitterSum / (latencies.length - 1) : 0;

    const successRate = ((pings - failures) / pings) * 100;

    return {
      min: Math.round(min),
      max: Math.round(max),
      avg: Math.round(avg),
      jitter: Math.round(jitter),
      successRate: Math.round(successRate),
      latencies
    };
  }
}
